import * as React from 'react';
import { Fragment, useContext, useEffect, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { addressSuggeestions } from '../../services/maps-api';
import { autocompleteSearch } from '../../services/prek-school-api';
import { celOverlays, cpsLegend, getOverlays, prekLegend, progLegend, getCPSYear } from '../results/map-container-config';
import { AppContext } from '../school-search-app';
import { FilterAction } from '../../services/filter-controller';
import { MAP_CLEAR_PIN } from '../../services/map-controller';
import { PAGE_MODE } from '../../interfaces';


type SchoolAutocompleteItem = {
    displayName: string;
    fieldName: string;
    fieldValue: string;
};

function SearchBar({ setIds, searchSchools, setViewMode, nearMeSearch, resetCount, termOverride, showHelp }) {
    const [search, setSearch] = useState('')
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selected, setSelected] = useState([]);
    const [allItems, setAllItems] = useState([]);
    const appContext = useContext(AppContext);
    const [overlays, setOverlays] = useState([]);

    useEffect(() => {
        getCPSYear()
            .then(_ => getOverlays(appContext.mode).then(o => setOverlays(o)))
            .catch(_ => getOverlays(appContext.mode).then(o => setOverlays(o)))

    }, []);

    const typeaheadRef = React.useRef();

    let legend;
    switch (appContext.mode) {
        case (PAGE_MODE.GOCPS):
            legend = cpsLegend;
            break;
        //case ('prog'):
        //    legend = progLegend;
        //    break;
        case (PAGE_MODE.PREK):
            legend = prekLegend;
            break;
    }

    const filterAutocompleteResults = (term, schools) => {
        const results = schools.filter(r => {
            if (r.fieldName == 'SchoolCPSCode' || r.fieldName == 'SchoolId') {
                return r.displayName && r.displayName.toLowerCase().includes(term.toLowerCase())
            }
            return r.displayName && r.displayName.toLowerCase().startsWith(term.toLowerCase())
        })
        if (results.length) {
            return results;
        }
        return [{
            "displayName": `Address: ${term}`,
            "fieldName": "AddressSearch",
            "fieldValue": term
        }]
    }

    useEffect(() => {
        setSearch(termOverride)
        setSelected([termOverride])
    }, [termOverride])

    const getSchoolsAutocomplete = async () => {
        try {
            let autocompleteEndpoint = "/api/v1/search/";
            switch (appContext.mode) {
                case PAGE_MODE.GOCPS:
                    autocompleteEndpoint += "autocompletelist?schoolType=cps";
                    break;
                case PAGE_MODE.SUMMER_PROGRAMM:
                    autocompleteEndpoint += "summerschoolautocompletelist";
                    break;
                default:
                    autocompleteEndpoint += "autocompletelist?schoolType=cel";
                    break;
            }
            const res = await fetch(autocompleteEndpoint).then((res) => res.json());
            return res;
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        const query = new URLSearchParams((window.location.search).toLowerCase());
        const schoolId = query.get("schoolsearchid")
        if (schoolId) {
            getSchoolsAutocomplete().then((list) => {
                const schoolList: SchoolAutocompleteItem[] = [...list];
                const schoolItem = schoolList.find(
                    (item) => schoolId.toString() === item.fieldValue.toString()
                );

                if (schoolItem) {
                    const match = [
                        {
                            displayName: schoolItem.displayName,
                            fieldName: schoolItem.fieldName,
                            fieldValue: schoolItem.fieldValue,
                        },
                    ];
                    setTimeout(() => {
                        handleSubmit(match);
                    }, 2500);
                    // if (appContext.isMapLoaded) {
                    //     handleSubmit(match);
                    // } else {
                    // }
                }
            });
        }
    }, []);

    const runAutocompleteSearch = (match) => {
        if (match.fieldName == 'AddressSearch') {
            setSearch(match.fieldValue);
            searchSchools(match.fieldValue);
            return;
        }

        history.replaceState("school", "School", window.location.pathname)

        if (!['PostalCode', 'Community'].some(i => i == match.fieldName) && match.fieldValue) {
            history.replaceState("school", "School", `?schoolSearchId=${match.fieldValue}`)
        }
        return autocompleteSearch(match, appContext.mode).then(res => {
            appContext.filterControllerDispatch({ action: FilterAction.RESET });
            appContext.setIsShowFilterWindow(false);
            setIds(res.map(s => s.id))
            if (match.fieldName == 'PostalCode') {
                appContext.addZipOverlay(match.fieldValue)
            }
            if (match.fieldName == 'Community') {
                appContext.addCommunityOverlay(match.fieldValue)
            }
            if (match.fieldName == 'SchoolCPSCode') {
                appContext.setFocusedSchool(res[0].id)
                appContext.mapControllerDispatch({ action: MAP_CLEAR_PIN })
            }
            if (appContext.mode == PAGE_MODE.GOCPS && match.fieldName == 'SchoolCPSCode') {
                appContext.addSchoolOverlay(res[0].id)
            }
            if (appContext.mode == PAGE_MODE.SUMMER_PROGRAMM) {
                appContext.setFocusedSchool(res[0].id)
                appContext.mapControllerDispatch({ action: MAP_CLEAR_PIN })
            }
        });
    }



    const handleChange = (event) => {
        if (event.key == 'Enter') {
            // @ts-ignore
            typeaheadRef && typeaheadRef.current.blur();
            let term = event.target.value;
            // check to see if we can do this with an autocompelte
            // if it's an exact match, select that match
            const match = items.filter(r => term.toLowerCase() == r.displayName.toLowerCase());
            if (match.length == 1) {
                setSearch(match[0].fieldValue)
                setSelected([match[0].displayName])
                return runAutocompleteSearch(match[0]);
            }
            // if there is only one match, try that one
            if (items.length == 1) {
                setSelected([items[0].displayName])
                if (items[0].fieldName == 'AddressSearch') {
                    // pass it up to text based (address)
                    setSearch(term)
                    searchSchools(term)
                    return;
                }
                return runAutocompleteSearch(items[0]);
            }

            if (items.length > 0) {
                setSearch(items[0].fieldValue)
                setSelected([items[0].displayName])
                return runAutocompleteSearch(items[0]);
            }
            // otherwise pass it up to text based (address)
            setSearch(term)
            searchSchools(term)
        }

    }

    const handleSubmit = (event) => {
        setSelected(event)
        if (event.length == 0) {
            return;
        }
        return runAutocompleteSearch(event[0]);
    }


    const handleSearch = (searchTerm) => {
        // @todo add in cancelling in progress searches
        setIsLoading(true)
        setItems([])
        setSearch(searchTerm)
        let autocompleteEndpoint = '/api/v1/search/';
        switch (appContext.mode) {
            case PAGE_MODE.GOCPS:
                autocompleteEndpoint += 'autocompletelist?schoolType=cps';
                break;
            case PAGE_MODE.SUMMER_PROGRAMM:
                autocompleteEndpoint += 'summerschoolautocompletelist';
                break;
            default:
                autocompleteEndpoint += 'autocompletelist?schoolType=cel';
                break;
        }
        const itemsPromise = allItems.length ? Promise.resolve(allItems) :
            fetch(autocompleteEndpoint).
                then(res => res.json()).
                then(list => {
                    setAllItems(list);
                    return list
                });

        const addressPromise = addressSuggeestions(searchTerm);

        return Promise.all([itemsPromise, addressPromise]).then(([list, addresses]) => {
            const addressItems = addresses.map(a => ({
                "displayName": `Address: ${a}`,
                "fieldName": "AddressSearch",
                "fieldValue": a
            }))
            setItems([...addressItems, ...filterAutocompleteResults(searchTerm, list)]);
            setIsLoading(false);
        })

    }

    // TODO refacotr all of this into reducers
    useEffect(() => {
        setSelected([])
    }, [resetCount])


    const renderTypeaheadItem = (option, props) => {
        const iconMap = [
            { fieldName: 'SchoolCPSCode', icon: 'school' },
            { fieldName: 'AddressSearch', icon: 'house' },
            { fieldName: 'Community', icon: 'city' },
        ]
        const icon = iconMap.find(i => i.fieldName == option.fieldName);
        const html = `<span class="fa fa-${icon ? icon.icon : 'search'}"></span> 
${option.displayName.replace(props.text, `<strong>${props.text}</strong>`)}`;
        return <Fragment>
            <span dangerouslySetInnerHTML={{ __html: html }}></span>
        </Fragment>
    }

    return <>{
        <div className="search-bar bs-shim">
            <div className="search-form-area">
                <form className="search-form" onSubmit={handleSubmit}>
                    <AsyncTypeahead
                        id="basic-typeahead-single"
                        labelKey="displayName"
                        onChange={(e) => handleSubmit(e)}
                        options={items}
                        placeholder="Enter Your Address, Site, School, Zip, or Neighborhood"
                        isLoading={isLoading}
                        onSearch={handleSearch}
                        onKeyDown={handleChange}
                        ref={typeaheadRef}
                        renderMenuItemChildren={renderTypeaheadItem}
                        filterBy={() => true}
                        selected={selected}
                        useCache={false}
                    />
                </form>
                <div className="search-button" onClick={() => handleChange({ key: 'Enter', target: { value: search } })}>
                    <i className="fa fa-search school-search-icon"></i>
                </div>
            </div>
            <div className="search_near_me">
                <Button onClick={nearMeSearch}><i className="fa fa-location-arrow"></i> Schools Near Me</Button>
            </div>
            <div className="expand_nav_btn">
                <Button onClick={appContext.toggleMaximize}>{appContext.maximized ?
                    <><i className="fa fa-compress-alt"></i> Contract</> :
                    <><i className="fa fa-expand-alt"></i> Expand</>}</Button>
            </div>

            {appContext.mode == PAGE_MODE.SUMMER_PROGRAMM ? <></> :
                <div className="view-buttons">
                    <Button onClick={appContext.toggleViewMode}>
                        {appContext.viewMode == 'cards' ?
                            <><i className="fa fa-bars"></i> List View</> :
                            <><i className="fa fa-th"></i> Card View</>
                        }
                    </Button>
                </div>
            }

            <div className="search-overlays-btn">
                <Dropdown>
                    <Dropdown.Toggle>
                        Overlays
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {overlays.map((group, groupIdx) => <Fragment key={`group-${groupIdx}`}>
                            {group.groupName && <Dropdown.Header>{group.groupName}</Dropdown.Header>}
                            {group.options.map((option, optionIdx) => <Dropdown.Item key={`option-${groupIdx}-${optionIdx}`} onClick={() => appContext.selectOverlay(option)}>
                                {option.label}
                            </Dropdown.Item>)}
                        </Fragment>)}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {appContext.mode == PAGE_MODE.SUMMER_PROGRAMM ? <></> : <div className="legend search_legend_btn">
                <Dropdown>
                    <Dropdown.Toggle>
                        Legend
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Header>
                            {legend.map((group, groupIdx) => <Fragment key={`group-${groupIdx}`}>
                                {group.groupName && <div className="dropdown-header" role="heading">{group.groupName}</div>}
                                <ul>
                                    {group.options.map((option, optionIdx) => <li key={`option-${groupIdx}-${optionIdx}`}>
                                        <img src={option.icon} />{option.label}
                                    </li>)}
                                </ul>
                            </Fragment>)}
                        </Dropdown.Header>
                    </Dropdown.Menu>
                </Dropdown>
            </div>}

            <div className="help-btn">
                <Button onClick={() => showHelp(true)}>Help</Button>
            </div>
        </div>
    }
    </>
}

export default SearchBar;